import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import HeadTitle from '../../components/HeadTitle/HeadTitle'
import DataTable from '../../components/NewDataTable/NewDataTable'
import api from '../../utils/Requester'
import { MAIL_ARCHIVE, RESEND_MAIL } from '../../constants/url'
import { useCookies } from 'react-cookie'
import { PLATFORM_ID_COOKIE } from '../../constants/cookies'
import { useSnackbar } from 'notistack'
import moment from 'moment'
import i18next from '../../i18n'
import { bindParams } from '../../utils/Link'
import GetApp from '@material-ui/icons/GetApp'
import AutoRenew from '@material-ui/icons/Autorenew'
import { IconWithSubtext } from '../PdP/ParticipatingCompanies/components/DWPIcons'
import { makeStyles } from '@material-ui/styles'
import { downloadFile } from '../../containers/DataContainer'
import { getFileNameToHeaders, managedFileNotFoundError } from '../../utils/File'
import fileDownload from 'js-file-download'
import { Tooltip, Modal, Typography, Button, Dialog, DialogTitle, DialogContent, Link } from '@material-ui/core'
import classNames from 'classnames'
import { COLUMNS_MAILS_COMMUNICATION, getIndexOfColumn } from '../../utils/Datatable'
import { useSelector } from 'react-redux'
import ROLES from '../../constants/roles'

const useStyles = makeStyles((theme) => ({
	paper: {
		margin: 'auto',
		marginTop: '10%',
		maxWidth: '60%',
		minWidth: 650,
		backgroundColor: theme.palette.background.paper,
		boxShadow: theme.shadows[5],
		outline: 'none',
		padding: 25,
	},
	title: {
		color: theme.palette.secondary.main,
		marginBottom: 20,
	},
	icon: {
		color: theme.palette.secondary.main,
		display: 'block',
		marginRight: 'auto',
		'&:hover': {
			cursor: 'pointer',
		},
	},
	iconInvalid: {
		opacity: 0.5,
		'&:hover': {
			cursor: 'not-allowed',
		},
	},
	buttonGroup: {
		display: 'flex',
	},
	button: {
		backgroundColor: theme.palette.primary.main,
		borderColor: theme.palette.secondary.main,
		color: theme.palette.secondary.main,
		borderRadius: 30,
		border: 'solid 2px',
		padding: 7,
		paddingLeft: 25,
		paddingRight: 25,
		marginBottom: 10,
	},
	validateButton: {
		marginLeft: 'auto',
	},
}))

function MailCommunication() {
	const { t } = useTranslation(['user', 'communication'])
	const [cookies] = useCookies()
	const { enqueueSnackbar } = useSnackbar()
	const classes = useStyles()
	const roles = useSelector((state) => state.roles.roles)?.accountTypes

	const MAIL_STATUSES = {
		Sent: {
			id: 1,
			label: t('communication:status_sent'),
			icon: 'send',
			color: '#4CAF50'
		},
		Open: {
			id: 2,
			label: t('communication:status_open'),
			icon: 'drafts',
			color: '#4CAF50'
		},
		Click: {
			id: 3,
			label: t('communication:status_click'),
			icon: 'drafts',
			color: '#4CAF50'
		},
		Blocked: {
			id: 4,
			label: t('communication:status_blocked'),
			icon: 'unsubscribe',
			color: '#e02214'
		},
		Spam: {
			id: 5,
			label: t('communication:status_spam'),
			icon: 'unsubscribe',
			color: '#e02214'
		}
	}

	const [datatable, setDatatable] = useState({ columns: [], data: [], options: {} })
	const [isLoading, setIsLoading] = useState(true)
	const [open, setOpen] = useState(false)
	const [mailId, setMailId] = useState(null)
	const [modalErrorState, setModalErrorState] = useState(false)
	const [modalErrorText, setModalErrorText] = useState('')

	const getMailArchive = () => {
		setIsLoading(true)
		api.get(bindParams(MAIL_ARCHIVE, cookies[PLATFORM_ID_COOKIE])).then((res) => {
			const { ok, data } = res

			if (ok) {
				if (data) {
					const newDatas = data
					if (!canResendMail()) {
						newDatas.columns = newDatas.columns.filter((x) => x.name !== 'message')
					}
					setDatatable(newDatas)
				}
			} else enqueueSnackbar(t('user:view_render_failed'), { variant: 'error' })

			setIsLoading(false)
		})
	}

	useEffect(() => {
		getMailArchive()
	}, [roles])

	const customColumns = [
		{
			name: 'date',
			component: (value) => {
				moment.locale(i18next.language)
				return moment(value).format('DD/MM/YYYY HH:mm')
			},
		},
		{
			name: 'attachment',
			component: function componentDocument(value) {
				if (!value) return t('communication:no_document')
				return (
					<Tooltip title={t('download')}>
						<GetApp
							classes={{ root: classes.icon }}
							onClick={() => {
								downloadDocument(value)
							}}
						/>
					</Tooltip>
				)
			},
		},
		{
			name: 'status',
			component: function componentStatus(value, tableMeta) {
				if (!value) return null
				const status = Object.values(MAIL_STATUSES).find((s) => s.id === value)
				if (!status) return value

				const messageError =
					tableMeta.rowData[getIndexOfColumn(datatable.columns, 'messageError')]

				const statusIcon = (
					<IconWithSubtext
						icon={status.icon}
						label={status.label}
						color={status.color}
						id={status.id}
						center={false}
					/>
				)

				return messageError ? (
					<Link
						href="#"
						onClick={(e) => {
							e.preventDefault()
							openErrorModal(messageError)
						}}
					>
						{statusIcon}
					</Link>
				) : (
					statusIcon
				)
			},
		},
		{
			name: 'message',
			component: function componentDocument(value, tableMeta) {
				return canResendMail() ? (
					<Tooltip title={t('resend')}>
						<AutoRenew
							className={classNames(classes.icon)}
							onClick={() => {
								openConfirm(
									tableMeta.rowData[
									getIndexOfColumn(datatable.columns, COLUMNS_MAILS_COMMUNICATION.ID)
									],
								)
							}}
						/>
					</Tooltip>
				) : null
			},
		},
	]

	const openConfirm = (mailId) => {
		const data = { mailArchiveId: mailId, platformId: cookies[PLATFORM_ID_COOKIE] }
		setOpen(true)
		setMailId(data)
	}

	const close = () => {
		setOpen(false)
		setMailId(null)
	}

	const openErrorModal = (errorText) => {
		setModalErrorText(errorText)
		setModalErrorState(true)
	}

	const resendMail = () => {
		if (canResendMail()) {
			api.post(RESEND_MAIL, mailId).then((res) => {
				const { ok } = res

				if (ok) {
					getMailArchive()
					enqueueSnackbar(t('communication:mail_resent'), { variant: 'success' })
				} else enqueueSnackbar(t('communication:mail_not_resent'), { variant: 'error' })

				close()
			})
		}
	}

	const downloadDocument = (file) => {
		downloadFile(file, function (res, error) {
			if (error) {
				managedFileNotFoundError(error, t, enqueueSnackbar)
				if (error.data && error.data.code) {
					enqueueSnackbar(t(error.data[0].code), { variant: 'error' })
					return
				}
				enqueueSnackbar(t('error') + ` : ${error.status} ! `, { variant: 'error' })
				return
			}
			if (res) {
				let fileName = getFileNameToHeaders(res.headers)
				fileDownload(res.data, fileName)
			}
		})
	}

	const canResendMail = () =>
		roles?.some((r) => r.code === ROLES.ADMIN.code || r.code === ROLES.PLATFORMMANAGER.code)

	return (
		<>
			<Dialog open={modalErrorState} onClose={() => setModalErrorState(false)} maxWidth="md">
				<DialogTitle>
					<Typography variant="h6" classes={{ root: classes.title }}>
						{t('user:error')}
					</Typography>
				</DialogTitle>
				<DialogContent>{modalErrorText}</DialogContent>
			</Dialog>
			<HeadTitle title={t('user:mail_communication_title')} />
			<DataTable
				title="Suivi"
				columns={[...datatable.columns]}
				data={datatable.data}
				options={datatable.options}
				customColumns={customColumns}
				isLoading={isLoading}
				customLabels={[
					{
						name: 'recipients',
						label: t('user:mail_communication_thead_recipients'),
					},
					{
						name: 'subject',
						label: t('user:mail_communication_thead_subject'),
					},
					{
						name: 'date',
						label: t('user:mail_communication_thead_date'),
					},
					{
						name: 'platformName',
						label: t('user:mail_communication_thead_platform_name'),
					},
					{
						name: 'userFullName',
						label: t('user:mail_communication_thead_user_full_name'),
					},
					{
						name: 'attachment',
						label: t('user:document'),
					},
					{
						name: 'message',
						label: t('user:resend'),
					},
					{
						name: 'status',
						label: t('user:mail_communication_thead_status')
					}
				]}
			/>
			<Modal open={open}>
				<div className={classes.paper}>
					<Typography variant="h6" classes={{ root: classes.title }}>
						{t('user:mailCommunication_title')}
					</Typography>
					<Typography variant="body1" classes={{ root: classes.title }}>
						{t('user:mailCommunication_body')}
					</Typography>
					<div className={classes.buttonGroup}>
						<Button classes={{ root: classes.button }} onClick={close}>
							{t('cancel')}
						</Button>
						<Button
							className={classNames(classes.button, classes.validateButton)}
							onClick={resendMail}
						>
							{t('submit')}
						</Button>
					</div>
				</div>
			</Modal>
		</>
	)
}
export default MailCommunication
